import { FC, RefObject, useCallback, useEffect, useRef, useState } from "react";
import { TagInputProps } from "./types";
import { Column, Row } from "../../containers";
import { AlignItems, JustifyContent, Wrap } from "../../containers/types";
import { Heading, Text } from "../../typography";
import { useGeneralStore, useThemeStore } from "../../../stores";
import Tag from "../../tag";
import { IconAnimation } from "../../animation";
import { CloseIcon } from "../../../assets/icons";
import { type Tag as TagType } from "../../../types";

const TagInput: FC<TagInputProps> = ({
  label,
  setValue,
  value,
  props,
  className = "",
  style = {},
  background,
  color,
  inputProps,
  readOnly = false,
}) => {
  const theme = useThemeStore((s) => s.theme);
  const tags = useGeneralStore((s) => s.tags);

  const [tagName, setTagName] = useState<string>("");
  const [foundTags, setFoundTags] = useState<Array<TagType>>([]);
  const [isFocus, setIsFocus] = useState<boolean>(false);

  useEffect(() => {
    if (!isFocus) {
      setTimeout(() => {
        setFoundTags([]);
      }, 50);
      return;
    }
    setFoundTags(
      tags.filter(
        (t) =>
          t.name.toLowerCase().includes(tagName.toLowerCase()) &&
          !value.includes(t._id.$oid)
      )
    );
  }, [isFocus, tagName, tags, value]);

  const ref: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

  const handler = useCallback(
    (e: MouseEvent): void => {
      if ((e.target as Element).tagName.toLowerCase() !== "input") {
        ref.current?.blur();
      }
    },
    [ref]
  );

  useEffect(() => {
    window.addEventListener("click", handler);
    return (): void => {
      window.removeEventListener("click", handler);
    };
  }, [handler]);

  return (
    <Column
      alignItems={AlignItems.Start}
      justifyContent={JustifyContent.Start}
      gap={5}
      className={className}
      style={{ width: "100%", position: "relative", ...style }}
      props={props}
    >
      <Heading color={color || theme.textOnOverlay} size={16}>
        {label}
      </Heading>
      <Row
        style={{
          borderRadius: !foundTags.length ? 15 : "15px 15px 0 0",
          background: background || theme.background,
          width: "calc(100% - 20px)",
          minHeight: 21,
          padding: "10px",
        }}
        flexWrap={Wrap.Wrap}
        gap={10}
      >
        {value.map((tag) => (
          <Row key={tag} gap={5}>
            <Tag>{{ $oid: tag }}</Tag>
            <IconAnimation
              Icon={CloseIcon}
              onHoverColor={theme.alert}
              className="cursor-pointer"
              onClick={() => {
                if (readOnly) return;
                setValue((prev) => prev.filter((t) => t !== tag));
              }}
            />
          </Row>
        ))}
        <input
          ref={ref}
          value={tagName}
          onChange={(e) => setTagName(e.target.value)}
          style={{
            height: 21,
            color:
              background === theme.background
                ? theme.textOnBg
                : theme.textOnOverlay,
            background: "transparent",
          }}
          placeholder="تگ خود را اینجا بنویسید"
          readOnly={readOnly}
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
          {...inputProps}
        />
      </Row>
      <Column
        style={{
          maxHeight: 150,
          overflowY: "auto",
          width: "100%",
          position: "absolute",
          top: "100%",
          left: 0,
          borderRadius: "0 0 15px 15px",
          boxShadow: `0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)`,
        }}
        background={background || theme.background}
      >
        {foundTags.map((ft) => (
          <Row
            key={ft._id.$oid}
            style={{
              padding: "0.5rem",
              width: "calc(100% - 1rem)",
              cursor: "pointer",
              borderTop: `1px solid ${theme.icon}`,
            }}
            props={{
              onClick: () => {
                if (readOnly) return;
                setValue((prev) => [ft._id.$oid, ...prev]);
                setTagName("");
              },
            }}
            justifyContent={JustifyContent.Center}
          >
            <Text
              color={
                background === theme.background
                  ? theme.textOnBg
                  : theme.textOnOverlay
              }
            >
              {ft.name}
            </Text>
          </Row>
        ))}
      </Column>
    </Column>
  );
};

export default TagInput;
